/**
 * CartIconState - Custom element to manage the header cart icon state
 *
 * This element listens to cart change events and updates the item count shown
 * inside the bag (hidden when the cart is empty). When both icons are present it
 * also toggles between filled and outline cart icons.
 *
 * @example
 * <cart-icon-state>
 *   {%- render 'icon' with 'cart' -%}
 *   <span class="header__cart-count" hidden>0</span>
 * </cart-icon-state>
 */

class CartIconState extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
  }

  connectedCallback() {
    this.abortController = new AbortController();

    // Listen for cart change events
    document.addEventListener(
      "cart:change",
      (event) => {
        this.updateIconState(event.detail.cart.item_count);
      },
      { signal: this.abortController.signal }
    );

    // Listen for cart refresh events
    document.addEventListener(
      "cart:refresh",
      () => {
        this.updateFromServer();
      },
      { signal: this.abortController.signal }
    );

    // Listen for page show events (back/forward navigation)
    window.addEventListener(
      "pageshow",
      () => {
        this.updateFromServer();
      },
      { signal: this.abortController.signal }
    );

    // Initialize icon state based on current cart count
    this.initializeIconState();
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  /**
   * Updates the icon state based on cart item count
   * @param {number} itemCount - Number of items in cart
   */
  updateIconState(itemCount) {
    // Count shown inside the bag, hidden when the cart is empty
    const countElement = this.querySelector(".header__cart-count");
    if (countElement) {
      countElement.textContent = itemCount;
      countElement.hidden = itemCount === 0;
    }

    this.setAttribute("data-has-items", itemCount > 0 ? "true" : "false");

    // Filled / outline icon swap is optional (the header with icons uses a single outline bag)
    const emptyIcon = this.querySelector(".cart-icon-empty");
    const filledIcon = this.querySelector(".cart-icon-filled");

    if (!emptyIcon || !filledIcon) return;

    emptyIcon.style.display = itemCount > 0 ? "none" : "block";
    filledIcon.style.display = itemCount > 0 ? "block" : "none";
  }

  /**
   * Fetches current cart data from server and updates icon state
   */
  async updateFromServer() {
    try {
      // Use the global fetchCart function if available, or fetch manually
      let cartData;
      if (typeof fetchCart === "function") {
        cartData = await fetchCart();
      } else {
        const response = await fetch("/cart.js");
        cartData = await response.json();
      }

      this.updateIconState(cartData.item_count);
    } catch (error) {
      console.error("CartIconState: Failed to fetch cart data", error);
    }
  }

  /**
   * Initialize icon state on component load
   */
  initializeIconState() {
    // Check if we already have the correct data-has-items attribute from Liquid
    const currentDataHasItems = this.getAttribute("data-has-items");

    if (currentDataHasItems !== null) {
      // We already have the initial state from Liquid, no need to override
      return;
    }

    // Fallback: Try to get initial cart count from existing cart-count element
    const cartCountElement = document.querySelector("cart-count");
    if (cartCountElement) {
      const itemCount = parseInt(cartCountElement.textContent) || 0;
      this.updateIconState(itemCount);
    } else {
      // Fallback to server fetch
      this.updateFromServer();
    }
  }
}

// Register the custom element if not already defined
if (!window.customElements.get("cart-icon-state")) {
  window.customElements.define("cart-icon-state", CartIconState);
}
